<script setup>
import {computed, nextTick, onBeforeUnmount, onMounted, ref, watch} from 'vue';
import {useRoute, useRouter} from 'vue-router';
import {requestOshiTags} from '../services/oshiTagApi';

const props = defineProps({initialData: {type: Object, required: true}});
const route = useRoute();
const router = useRouter();
const labels = props.initialData.labels;
const catalog = ref(null);
const mode = ref('refinement');
const selected = ref([]);
const draft = ref([]);
const items = ref([]);
const matchedCount = ref(null);
const hasMore = ref(false);
const cursor = ref(null);
const loading = ref(false);
const error = ref('');
const loaded = ref(false);
const sentinel = ref(null);
let revision = 0;
let countRevision = 0;
let observer;
let stopRouting;
const tags = computed(() => catalog.value?.parents.flatMap(parent => parent.tags) ?? []);
const knownTags = new Map();
const selectedIds = computed(() => mode.value === 'refinement' ? draft.value : selected.value);
const recommended = computed(() => (catalog.value?.recommendedTagIds ?? []).map(id => tags.value.find(tag => tag.id === id)).filter(Boolean));
const tagInfo = id => knownTags.get(id) ?? {id, name: labels.unknown_tag.replace('%id%', String(id))};
const format = (key, value) => labels[key].replace('%count%', String(value));
const messageFor = failure => failure.status === 404 ? labels.invalid_tag : failure.status === 400 ? labels.invalid_input : labels.unavailable;

function queryIds() {
  const indexed = Object.entries(route.query).filter(([key]) => /^tag_ids\[[0-9]+\]$/.test(key)).flatMap(([, value]) => value);
  const raw = route.query['tag_ids[]'] ?? route.query.tag_ids ?? (indexed.length ? indexed : undefined);
  if (raw === undefined) return [];
  const values = Array.isArray(raw) ? raw : [raw];
  if (values.some(id => typeof id !== 'string' || !/^[1-9][0-9]*$/.test(id) || !Number.isSafeInteger(Number(id)))) throw new Error('Invalid tags');
  return [...new Set(values.map(Number))].sort((a, b) => a - b);
}

async function fetchCatalog() {
  const response = await requestOshiTags(props.initialData.catalogUrl);
  catalog.value = response;
  for (const tag of tags.value) knownTags.set(tag.id, tag);
}

async function restore() {
  const current = ++revision;
  ++countRevision;
  loading.value = false;
  loaded.value = false;
  error.value = '';
  items.value = [];
  cursor.value = null;
  hasMore.value = false;
  matchedCount.value = null;
  try { selected.value = queryIds(); }
  catch { error.value = labels.invalid_input; return; }
  const state = window.history.state;
  draft.value = selected.value.length === 0 && state?.oshiTagMode === 'refinement'
    && Array.isArray(state.oshiTagDraft) ? [...state.oshiTagDraft] : [];
  mode.value = selected.value.length ? 'results' : 'refinement';
  if (mode.value === 'refinement' && !draft.value.length) matchedCount.value = 0;
  try {
    if (!catalog.value) await fetchCatalog();
    if (current !== revision) return;
    if (mode.value === 'results') await loadUsers(false);
    else if (draft.value.length) await previewCount();
  } catch (failure) { if (current === revision) error.value = messageFor(failure); }
}

async function loadUsers(append = false, allowRefresh = true) {
  if (loading.value || mode.value !== 'results' || (append && !hasMore.value)) return;
  const current = revision;
  loading.value = true;
  error.value = '';
  try {
    const result = await requestOshiTags(props.initialData.usersUrl, {
      tagIds: [...selected.value], generation: catalog.value.generation, cursor: append ? cursor.value : null,
    });
    if (current !== revision) return;
    const existing = new Set(append ? items.value.map(item => item.publicCustomerId) : []);
    const additions = result.items.filter(item => {
      if (existing.has(item.publicCustomerId)) return false;
      existing.add(item.publicCustomerId);
      return true;
    });
    items.value = append ? [...items.value, ...additions] : additions;
    cursor.value = result.nextCursor;
    hasMore.value = result.hasMore;
    loaded.value = true;
  } catch (failure) {
    if (current !== revision) return;
    if (failure.status === 409 && allowRefresh) {
      try {
        await fetchCatalog();
        if (current !== revision) return;
        items.value = [];
        cursor.value = null;
        loaded.value = false;
        loading.value = false;
        return await loadUsers(false, false);
      } catch (refreshFailure) { error.value = messageFor(refreshFailure); }
    } else error.value = messageFor(failure);
  } finally {
    if (current === revision) {
      loading.value = false;
      await nextTick();
      if (sentinel.value && observer) {
        observer.unobserve(sentinel.value);
        observer.observe(sentinel.value);
      }
    }
  }
}

async function previewCount(allowRefresh = true) {
  const current = ++countRevision;
  const view = revision;
  error.value = '';
  try {
    const result = await requestOshiTags(props.initialData.countUrl, {tagIds: [...draft.value], generation: catalog.value.generation});
    if (current === countRevision && view === revision) matchedCount.value = result.matchedCount;
  } catch (failure) {
    if (current !== countRevision || view !== revision) return;
    if (failure.status === 409 && allowRefresh) {
      try {
        await fetchCatalog();
        if (current === countRevision && view === revision) return await previewCount(false);
      } catch (refreshFailure) {
        matchedCount.value = null;
        error.value = messageFor(refreshFailure);
      }
    } else {
      matchedCount.value = null;
      error.value = messageFor(failure);
    }
  }
}

function results(ids) {
  return router.push({path: props.initialData.pageUrl, query: ids.length ? {'tag_ids[]': ids.map(String)} : {},
    state: {oshiTagMode: null, oshiTagDraft: null}});
}

async function selectTag(id) {
  draft.value = draft.value.includes(id) ? draft.value.filter(value => value !== id) : [...draft.value, id].sort((a, b) => a - b);
  window.history.replaceState({...window.history.state, oshiTagMode: draft.value.length ? 'refinement' : null,
    oshiTagDraft: draft.value.length ? [...draft.value] : null}, '');
  if (!draft.value.length) {
    ++countRevision;
    matchedCount.value = 0;
    error.value = '';
    return;
  }
  await previewCount();
}

function backToFilters() {
  return router.push({path: props.initialData.pageUrl, query: {},
    state: {oshiTagMode: 'refinement', oshiTagDraft: [...selected.value]}});
}

function chatTime(item) {
  return labels.time_range.replace('%from%', item.chatTimeFrom ?? '').replace('%to%', item.chatTimeTo ?? '')
    .replace('%timezone%', item.chatTimezone ?? '').trim();
}

watch(sentinel, element => { if (element && observer) observer.observe(element); });
onMounted(async () => {
  if (typeof IntersectionObserver !== 'undefined') {
    observer = new IntersectionObserver(entries => {
      if (entries.some(entry => entry.isIntersecting) && !error.value) loadUsers(true);
    }, {rootMargin: '200px'});
  }
  stopRouting = router.afterEach(() => restore());
  await restore();
});
onBeforeUnmount(() => { ++revision; ++countRevision; stopRouting?.(); observer?.disconnect(); });
</script>

<template>
  <div class="oshi-tag-page" :class="{'oshi-tag-page--refinement': mode === 'refinement'}">
    <div v-if="mode === 'results'" class="oshi-tag-selected oshi-tag-pills" :aria-label="labels.applied_tags">
      <button v-for="id in selected" :key="id" type="button" class="oshi-tag-pill" :aria-label="labels.remove.replace('%name%', tagInfo(id).name)" @click="results(selected.filter(value => value !== id))">
        <span>{{ tagInfo(id).name }}</span><span class="oshi-tag-pill__remove" aria-hidden="true">×</span>
      </button>
    </div>
    <header class="oshi-tag-page__heading" :class="{'oshi-tag-page__heading--with-back': mode === 'results'}">
      <button v-if="mode === 'results'" type="button" class="oshi-tag-back" :aria-label="labels.back" @click="backToFilters">＜</button>
      <h1>{{ labels.title }}</h1>
    </header>
    <div v-if="error" class="oshi-tag-error" role="alert">
      <p>{{ error }}</p>
    </div>
    <template v-if="mode === 'results'">
      <div v-if="loaded && !error && !items.length" class="oshi-tag-empty"><p>{{ labels.empty }}</p><p>{{ labels.clear_prompt }}</p></div>
      <div class="oshi-tag-users">
        <a v-for="item in items" :key="item.publicCustomerId" :href="item.detailUrl" class="oshi-tag-user">
          <img :src="item.iconUrl" alt="" loading="lazy">
          <div class="oshi-tag-user__body">
            <h2>{{ item.nickname }}</h2>
            <span class="oshi-tag-user__status" :class="{'is-open': item.chatEnabled}">{{ item.chatEnabled ? labels.chat_on : labels.chat_off }}</span>
            <p v-if="item.chatTimeFrom || item.chatTimeTo || item.chatTimezone" class="oshi-tag-user__time">{{ chatTime(item) }}</p>
            <p v-if="item.comment" class="oshi-tag-description">{{ item.comment }}</p>
          </div>
        </a>
      </div>
      <p v-if="loading" role="status">{{ labels.loading }}</p>
      <div v-if="hasMore && !error" ref="sentinel" class="oshi-tag-load">
        <button type="button" class="oshi-tag-button" :disabled="loading" @click="loadUsers(true)">{{ labels.load_more }}</button>
      </div>
    </template>
    <template v-else-if="catalog">
      <section v-if="recommended.length" class="oshi-tag-recommended">
        <div class="oshi-tag-page__heading"><h2>{{ labels.recommended }}</h2></div>
        <div class="oshi-tag-slider">
          <button v-for="tag in recommended" :key="tag.id" type="button" class="oshi-tag-card" :class="{'is-selected': selectedIds.includes(tag.id)}" :aria-pressed="selectedIds.includes(tag.id)" @click="selectTag(tag.id)">
            <img :src="tag.imageUrl" :alt="tag.name" loading="lazy">
          </button>
        </div>
      </section>
      <section v-for="parent in catalog.parents" :key="parent.id" class="oshi-tag-category">
        <h2>{{ parent.name }}</h2>
        <div class="oshi-tag-grid">
          <button v-for="tag in parent.tags" :key="tag.id" type="button" class="oshi-tag-card" :class="{'is-selected': selectedIds.includes(tag.id)}" :aria-pressed="selectedIds.includes(tag.id)" @click="selectTag(tag.id)">
            <img :src="tag.imageUrl" :alt="tag.name" loading="lazy">
          </button>
        </div>
      </section>
      <p v-if="!catalog.parents.length">{{ labels.no_tags }}</p>
      <footer v-if="mode === 'refinement'" class="oshi-tag-refinement">
        <div class="oshi-tag-pills">
          <button v-for="id in draft" :key="id" type="button" class="oshi-tag-pill" :aria-label="labels.remove.replace('%name%', tagInfo(id).name)" @click="selectTag(id)">
            <span>{{ tagInfo(id).name }}</span><span class="oshi-tag-pill__remove" aria-hidden="true">×</span>
          </button>
        </div>
        <div class="oshi-tag-refinement__actions"><div class="oshi-tag-refinement__count" aria-live="polite"><p>{{ format('selected', draft.length) }}</p><p>{{ matchedCount === null ? (error ? '—' : labels.loading) : format('matched', matchedCount) }}</p></div>
          <button type="button" class="oshi-tag-button oshi-tag-button--primary" :disabled="draft.length === 0" @click="results(draft)">{{ labels.apply }}</button></div>
      </footer>
    </template>
    <p v-else-if="!error" role="status">{{ labels.loading }}</p>
  </div>
</template>
